import { Injectable } from '@angular/core';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { NestedQuota } from '../../../shared/interfaces/quota.interface';
import { notifyMessage } from '../../../shared/constants/notify-message';

@Injectable({
  providedIn: 'root',
})
export class NestedQuotaService {
  constructor(private _toastr: ToasterService) {}

  isNestedQuotaInvalidInput(
    quota: NestedQuota,
    nestedQuotas: NestedQuota[],
    surveyGoal: number
  ) {
    const totalPercentage = nestedQuotas.reduce(
      (percentage, quota) => percentage + quota.percentage!,
      0
    );
    const totalAllocation = nestedQuotas.reduce(
      (count, quota) => count + (quota.required_count || quota.number || 0),
      0
    );

    if (totalAllocation > surveyGoal || totalPercentage > 100) {
      // maximum percentage can be 100 only
      this._toastr.warning(
        notifyMessage.warningMessage.QUOTA_MODAL.INVALID_QUOTA_COMPLETE
      );
    }
    if (
      (quota.percentage && quota.percentage > 100) ||
      quota.required_count > surveyGoal
    ) {
      return true;
    }
    return false;
  }

  updateQuotaPercentage(
    quota: NestedQuota,
    nestedQuotas: NestedQuota[],
    surveyGoal: number
  ) {
    if (this.isNestedQuotaInvalidInput(quota, nestedQuotas, surveyGoal)) {
      quota.percentage = quota.initialQuotaPercentage;
      return;
    }

    if (quota.percentage) {
      const totalCount = (surveyGoal * quota.percentage) / 100;
      quota.current_target = quota.required_count = Math.round(totalCount);
      quota.number = quota.current_target;
      quota.minimum = quota.maximum = quota.required_count;
    }
  }

  updateQuotaAllocation(
    quota: NestedQuota,
    nestedQuotas: NestedQuota[],
    surveyGoal: number
  ) {
    if (this.isNestedQuotaInvalidInput(quota, nestedQuotas, surveyGoal)) {
      this.setInitialAllocation(quota);
      return;
    }

    const percentage =
      (100 * (quota.required_count || quota.number || 0)) / surveyGoal;
    quota.percentage = Number(percentage.toFixed(2));
    quota.number = quota.current_target = quota.required_count;
    quota.minimum = quota.maximum = quota.required_count || quota.number;
  }

  setInitialAllocation(nestedQuota: NestedQuota) {
    if (
      (nestedQuota.required_count || nestedQuota.number) &&
      nestedQuota.initialQuotaAllocation
    ) {
      nestedQuota.required_count = nestedQuota.initialQuotaAllocation;
    }
  }

  calculateIndividualQuotaSum(nestedQuota: NestedQuota) {
    let individualQuotas = nestedQuota.individual_quotas || [];

    return individualQuotas.reduce((sum, quota) => {
      return sum + (quota.allocation ? quota.allocation.percentage : 0);
    }, 0);
  }

  updateIndividualQuotas(nestedQuota: NestedQuota, surveyGoal: number) {
    const { percentage = 0 } = nestedQuota;
    let individualQuotas = nestedQuota.individual_quotas || [];
    let sumOfQuotaPercentages = this.calculateIndividualQuotaSum(nestedQuota);

    individualQuotas?.forEach((quota) => {
      if (sumOfQuotaPercentages !== 0) {
        quota.allocation.percentage = Number(
          (
            (percentage * quota.allocation.percentage) /
            sumOfQuotaPercentages
          ).toFixed(2)
        );
      } else {
        quota.allocation.percentage = Number(
          Math.round(percentage / individualQuotas.length).toFixed(2)
        );
      }
      quota.allocation.quantity =
        quota.allocation.min =
        quota.allocation.max =
          Math.round((surveyGoal * quota.allocation.percentage) / 100);
    });
  }
}
